// ============================================
// fields/PhoneField.js - Split prefix and number fields
// ============================================
import React, { useState, useEffect, useRef } from 'react';

// Country data with dial codes and formats
const countryData = [
  { code: '+1', country: 'US/CA', flag: '🇺🇸', format: '(xxx) xxx-xxxx', regex: /^\+?1/, priority: 1 },
  { code: '+7', country: 'RU/KZ', flag: '🇷🇺', format: 'xxx xxx-xx-xx', regex: /^\+?7/, priority: 1 },
  { code: '+20', country: 'EG', flag: '🇪🇬', format: 'xx xxxx xxxx', regex: /^\+?20/ },
  { code: '+27', country: 'ZA', flag: '🇿🇦', format: 'xx xxx xxxx', regex: /^\+?27/ },
  { code: '+30', country: 'GR', flag: '🇬🇷', format: 'xxx xxx xxxx', regex: /^\+?30/ },
  { code: '+31', country: 'NL', flag: '🇳🇱', format: 'x xxxx xxxx', regex: /^\+?31/ },
  { code: '+32', country: 'BE', flag: '🇧🇪', format: 'xxx xx xx xx', regex: /^\+?32/ },
  { code: '+33', country: 'FR', flag: '🇫🇷', format: 'x xx xx xx xx', regex: /^\+?33/ },
  { code: '+34', country: 'ES', flag: '🇪🇸', format: 'xxx xxx xxx', regex: /^\+?34/ },
  { code: '+36', country: 'HU', flag: '🇭🇺', format: 'xx xxx xxxx', regex: /^\+?36/ },
  { code: '+39', country: 'IT', flag: '🇮🇹', format: 'xxx xxx xxxx', regex: /^\+?39/ },
  { code: '+40', country: 'RO', flag: '🇷🇴', format: 'xxx xxx xxx', regex: /^\+?40/ },
  { code: '+41', country: 'CH', flag: '🇨🇭', format: 'xx xxx xx xx', regex: /^\+?41/ },
  { code: '+43', country: 'AT', flag: '🇦🇹', format: 'xxx xxxxxx', regex: /^\+?43/ },
  { code: '+44', country: 'UK', flag: '🇬🇧', format: 'xxxx xxxxxx', regex: /^\+?44/ },
  { code: '+45', country: 'DK', flag: '🇩🇰', format: 'xxxx xxxx', regex: /^\+?45/ },
  { code: '+46', country: 'SE', flag: '🇸🇪', format: 'xx xxx xxxx', regex: /^\+?46/ },
  { code: '+47', country: 'NO', flag: '🇳🇴', format: 'xxx xx xxx', regex: /^\+?47/ },
  { code: '+48', country: 'PL', flag: '🇵🇱', format: 'xxx xxx xxx', regex: /^\+?48/ },
  { code: '+49', country: 'DE', flag: '🇩🇪', format: 'xxx xxxxxxxx', regex: /^\+?49/ },
  { code: '+52', country: 'MX', flag: '🇲🇽', format: 'xx xxxx xxxx', regex: /^\+?52/ },
  { code: '+55', country: 'BR', flag: '🇧🇷', format: 'xx xxxxx-xxxx', regex: /^\+?55/ },
  { code: '+61', country: 'AU', flag: '🇦🇺', format: 'x xxxx xxxx', regex: /^\+?61/ },
  { code: '+81', country: 'JP', flag: '🇯🇵', format: 'xx-xxxx-xxxx', regex: /^\+?81/ },
  { code: '+82', country: 'KR', flag: '🇰🇷', format: 'xx-xxxx-xxxx', regex: /^\+?82/ },
  { code: '+86', country: 'CN', flag: '🇨🇳', format: 'xxx xxxx xxxx', regex: /^\+?86/ },
  { code: '+90', country: 'TR', flag: '🇹🇷', format: 'xxx xxx xx xx', regex: /^\+?90/ },
  { code: '+91', country: 'IN', flag: '🇮🇳', format: 'xxxxx xxxxx', regex: /^\+?91/ },
  { code: '+234', country: 'NG', flag: '🇳🇬', format: 'xxx xxx xxxx', regex: /^\+?234/ },
  { code: '+351', country: 'PT', flag: '🇵🇹', format: 'xxx xxx xxx', regex: /^\+?351/ },
  { code: '+353', country: 'IE', flag: '🇮🇪', format: 'xx xxx xxxx', regex: /^\+?353/ },
  { code: '+358', country: 'FI', flag: '🇫🇮', format: 'xx xxx xxxx', regex: /^\+?358/ },
  { code: '+420', country: 'CZ', flag: '🇨🇿', format: 'xxx xxx xxx', regex: /^\+?420/ },
  { code: '+852', country: 'HK', flag: '🇭🇰', format: 'xxxx xxxx', regex: /^\+?852/ },
  { code: '+966', country: 'SA', flag: '🇸🇦', format: 'x xxx xxxx', regex: /^\+?966/ },
  { code: '+971', country: 'AE', flag: '🇦🇪', format: 'x xxx xxxx', regex: /^\+?971/ },
].sort((a, b) => b.code.length - a.code.length);

export const PhoneField = ({ field, register, errors, theme, setValue, watch, defaultCountryCode = '+1' }) => {
  // Find default country from the provided code
  const getDefaultCountry = () => {
    return countryData.find(c => c.code === defaultCountryCode) || countryData[0];
  };
  
  const defaultCountry = getDefaultCountry();
  
  // Initialize state with form value if it exists
  const initializeFromFormValue = (formValue) => {
    if (!formValue || formValue === '') {
      return {
        prefix: '',
        number: '',
        country: null,
        hasInput: false
      };
    }
    
    // Parse the existing form value
    for (const country of countryData) {
      if (formValue.startsWith(country.code)) {
        const remainingDigits = formValue.slice(country.code.length);
        return {
          prefix: country.code,
          number: formatPhoneNumber(remainingDigits, country),
          country: country,
          hasInput: true
        };
      }
    }
    
    // If no country code found, default to the defaultCountryCode
    const defaultCountryObj = getDefaultCountry();
    return {
      prefix: defaultCountryObj.code,
      number: formatPhoneNumber(formValue, defaultCountryObj),
      country: defaultCountryObj,
      hasInput: true
    };
  };
  
  // Get initial form value
  const initialFormValue = watch(field.id) || '';
  const initialState = initializeFromFormValue(initialFormValue);
  
  const [prefixValue, setPrefixValue] = useState(initialState.prefix);
  const [numberValue, setNumberValue] = useState(initialState.number);
  const [detectedCountry, setDetectedCountry] = useState(initialState.country);
  const [hasUserInput, setHasUserInput] = useState(initialState.hasInput);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [formatStatus, setFormatStatus] = useState('partial');
  const [isInitialized, setIsInitialized] = useState(false);
  
  const prefixRef = useRef(null);
  const numberRef = useRef(null);

  // Initialize from form value on mount and when returning to the page
  useEffect(() => {
    const currentValue = watch(field.id);
    
    // Only initialize if we haven't yet, or if the form value has changed externally
    if (!isInitialized && currentValue) {
      const state = initializeFromFormValue(currentValue);
      setPrefixValue(state.prefix);
      setNumberValue(state.number);
      setDetectedCountry(state.country);
      setHasUserInput(state.hasInput);
      setIsInitialized(true);
    }
  }, [watch(field.id), isInitialized]);

  // Detect country from prefix
  const detectCountryFromPrefix = (value) => {
    if (!value || value === '+') return null;
    
    const cleaned = value.replace(/[^\d+]/g, '');
    
    for (const country of countryData) {
      if (country.code === cleaned) {
        return country;
      }
      // Also check partial matches for longer codes
      if (country.code.startsWith(cleaned) && cleaned.length >= 2) {
        // Check if this might be a complete match
        const nextChar = country.code[cleaned.length];
        if (!nextChar || cleaned.length === country.code.length) {
          return country;
        }
      }
    }
    return null;
  };

  // Format phone number based on country
  function formatPhoneNumber(value, country) {
    if (!country || !value) return value;
    
    // Remove all non-digits
    let cleaned = value.replace(/\D/g, '');
    
    // Apply country-specific formatting
    if (country.code === '+1') {
      // US/Canada format
      if (cleaned.length <= 3) return cleaned;
      if (cleaned.length <= 6) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
    } else if (country.code === '+44') {
      // UK format
      if (cleaned.length <= 4) return cleaned;
      return `${cleaned.slice(0, 4)} ${cleaned.slice(4, 10)}`;
    } else if (country.code === '+33') {
      // France format
      const parts = cleaned.match(/.{1,2}/g);
      return parts ? parts.join(' ') : cleaned;
    } else {
      // Generic format: use the format string as a guide
      const format = country.format;
      let formatted = '';
      let digitIndex = 0;
      
      for (let i = 0; i < format.length && digitIndex < cleaned.length; i++) {
        if (format[i] === 'x') {
          formatted += cleaned[digitIndex];
          digitIndex++;
        } else if (format[i] === ' ' || format[i] === '-') {
          if (digitIndex > 0 && digitIndex < cleaned.length) {
            formatted += format[i];
          }
        }
      }
      
      // Add remaining digits if any
      if (digitIndex < cleaned.length) {
        formatted += cleaned.slice(digitIndex);
      }
      
      return formatted;
    }
  }

  const isValidFormat = (number, country) => {
    if (!country || !number) return false;
    
    // Remove all non-digits for validation
    const digitsOnly = number.replace(/\D/g, '');
    
    // Count expected digits from format (x's in the format string)
    const expectedDigits = country.format.replace(/[^x]/g, '').length;
    
    // Check if we have the right number of digits
    // Allow partial numbers (for typing) but highlight when complete
    if (digitsOnly.length === expectedDigits) {
      return 'complete';
    } else if (digitsOnly.length < expectedDigits) {
      return 'partial';
    } else {
      return 'invalid';
    }
  };

  // Update format status when number changes
  useEffect(() => {
    if (detectedCountry && numberValue) {
      setFormatStatus(isValidFormat(numberValue, detectedCountry));
    } else {
      setFormatStatus('partial');
    }
  }, [numberValue, detectedCountry]);

  // Handle prefix input
  const handlePrefixChange = (e) => {
    let value = e.target.value;
    
    // Mark that user has started typing
    if (!hasUserInput && value !== '') {
      setHasUserInput(true);
    }
    
    // If user clears the field, show placeholder again
    if (value === '') {
      setHasUserInput(false);
      setPrefixValue('');
      setDetectedCountry(null);
      setNumberValue('');
      updateFormValue('', '');
      return;
    }
    
    // Ensure it starts with +
    if (!value.startsWith('+')) {
      value = '+' + value.replace(/[^\d]/g, '');
    } else {
      value = '+' + value.slice(1).replace(/[^\d]/g, '');
    }
    
    // Limit to reasonable prefix length (max 5 chars including +)
    if (value.length > 5) {
      // Move excess to number field
      const excess = value.slice(5);
      value = value.slice(0, 5);
      setNumberValue(excess + numberValue);
    }
    
    setPrefixValue(value);
    
    // Check if we have a complete country code
    const country = detectCountryFromPrefix(value);
    if (country && value === country.code) {
      // Complete country code detected - transition to number field
      setDetectedCountry(country);
      setIsTransitioning(true);
      
      // Smooth transition to number field
      setTimeout(() => {
        numberRef.current?.focus();
        setIsTransitioning(false);
      }, 150);
    } else {
      setDetectedCountry(null);
    }
    
    // Update form value
    updateFormValue(value, numberValue);
  };

  // Handle prefix focus
  const handlePrefixFocus = () => {
    if (!hasUserInput) {
      // When focusing on empty field, prepare for input but don't set anything yet
      setPrefixValue('');
    }
  };

  // Handle prefix blur
  const handlePrefixBlur = () => {
    if (prefixValue === '' && !numberValue) {
      // Reset to showing placeholder when field is empty and unfocused
      setHasUserInput(false);
      setDetectedCountry(null);
    }
  };

  // Handle number input
  const handleNumberChange = (e) => {
    let value = e.target.value;
    
    // Just update the value normally - deletion should work as expected
    // Format based on detected country
    if (detectedCountry) {
      value = formatPhoneNumber(value, detectedCountry);
    } else {
      // Basic formatting without country detection
      value = value.replace(/[^\d\s\-()]/g, '');
    }
    
    setNumberValue(value);
    updateFormValue(prefixValue, value);
  };

  // Handle number field keyboard events
  const handleNumberKeyDown = (e) => {
    // Get current selection state
    const selectionStart = numberRef.current?.selectionStart || 0;
    const selectionEnd = numberRef.current?.selectionEnd || 0;
    const hasSelection = selectionStart !== selectionEnd;
    const isAtStart = selectionStart === 0 && selectionEnd === 0;
    
    // If text is selected and user presses delete/backspace, let it delete normally
    if ((e.key === 'Backspace' || e.key === 'Delete') && hasSelection) {
      // Don't prevent default - let the deletion happen
      return;
    }
    
    // Only move to prefix field if:
    // - Pressing backspace
    // - At the very beginning of the field (cursor at position 0)
    // - No text is selected
    // - Field is already empty
    if (
      e.key === 'Backspace' && 
      isAtStart &&
      !hasSelection &&
      (!numberValue || numberValue === '')
    ) {
      e.preventDefault();
      prefixRef.current?.focus();
      setTimeout(() => {
        if (prefixRef.current) {
          prefixRef.current.selectionStart = prefixRef.current.value.length;
          prefixRef.current.selectionEnd = prefixRef.current.value.length;
        }
      }, 0);
      return;
    }
    
    // Move back to prefix field on left arrow at beginning with no selection
    if (e.key === 'ArrowLeft' && isAtStart && !hasSelection) {
      e.preventDefault();
      prefixRef.current?.focus();
      setTimeout(() => {
        if (prefixRef.current) {
          prefixRef.current.selectionStart = prefixRef.current.value.length;
          prefixRef.current.selectionEnd = prefixRef.current.value.length;
        }
      }, 0);
    }
  };

  // Handle prefix field keyboard events
  const handlePrefixKeyDown = (e) => {
    // Move to number field on space, tab (default), or right arrow at end
    if (
      (e.key === ' ' || e.key === 'ArrowRight') && 
      prefixRef.current?.selectionStart === prefixValue.length
    ) {
      e.preventDefault();
      numberRef.current?.focus();
    }
  };

  // Update the form value
  const updateFormValue = (prefix, number) => {
    const cleanNumber = number.replace(/\D/g, '');
    const fullNumber = prefix + cleanNumber;
    setValue(field.id, fullNumber, { 
      shouldValidate: true,
      shouldDirty: true 
    });
  };

  return (
    <div>
      <div className="flex gap-2">
        {/* Prefix field */}
        <div className="relative flex-shrink-0">
          <div className={`
            relative overflow-hidden
            ${isTransitioning ? 'animate-pulse' : ''}
            transition-all duration-300
          `}>
            {/* Show flag - either detected country, or default greyed out */}
            <span className={`
              absolute left-2 top-1/2 -translate-y-1/2 text-xl pointer-events-none
              transition-opacity duration-200
              ${!hasUserInput && !prefixValue ? 'opacity-30' : 'opacity-100'}
            `}>
              {(detectedCountry || (!hasUserInput && defaultCountry))?.flag}
            </span>
            
            <input
              ref={prefixRef}
              type="text"
              value={hasUserInput ? prefixValue : (prefixValue || '')}
              onChange={handlePrefixChange}
              onFocus={handlePrefixFocus}
              onBlur={handlePrefixBlur}
              onKeyDown={handlePrefixKeyDown}
              placeholder={defaultCountry.code}
              className={`
                ${theme.input} 
                pl-10 pr-3
                ${errors[field.id] ? theme.inputError : ''}
                w-24 text-center font-mono
                transition-all duration-300
                ${detectedCountry ? 'bg-green-50 dark:bg-green-950 border-green-500' : ''}
                ${!hasUserInput && !prefixValue ? 'text-gray-400' : ''}
              `}
              disabled={field.disabled}
              maxLength={5}
            />
          </div>
        </div>

        {/* Number field */}
        <div className="relative flex-1">
          <input
            ref={numberRef}
            type="tel"
            value={numberValue}
            onChange={handleNumberChange}
            onKeyDown={handleNumberKeyDown}
            placeholder={
              detectedCountry 
                ? detectedCountry.format 
                : (!hasUserInput && defaultCountry 
                    ? defaultCountry.format 
                    : '(555) 123-4567')
            }
            className={`
              ${theme.input} 
              ${errors[field.id] ? theme.inputError : ''}
              w-full
              transition-all duration-300
              ${isTransitioning ? 'ring-2 ring-blue-400 ring-opacity-75' : ''}
              ${!detectedCountry && !hasUserInput ? 'opacity-75' : ''}
              ${detectedCountry && formatStatus === 'complete' ? 'bg-green-50 dark:bg-green-950 border-green-500' : ''}
              ${detectedCountry && formatStatus === 'invalid' ? 'border-orange-400' : ''}
            `}
            disabled={field.disabled || (!detectedCountry && hasUserInput)}
          />
          
          {/* Format validation indicator */}
          {detectedCountry && numberValue && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
              {formatStatus === 'complete' && (
                <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              )}
              {formatStatus === 'partial' && (
                <div className="text-xs text-gray-400">
                  {numberValue.replace(/\D/g, '').length}/{detectedCountry.format.replace(/[^x]/g, '').length}
                </div>
              )}
              {formatStatus === 'invalid' && (
                <svg className="w-5 h-5 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              )}
            </div>
          )}
          
          {/* Visual connection indicator */}
          {isTransitioning && (
            <div className="absolute -left-2 top-1/2 -translate-y-1/2 w-4 h-0.5 bg-blue-400 animate-pulse" />
          )}
        </div>

        {/* Hidden input for form value */}
        <input
          type="hidden"
          {...register(field.id, field.validation || defaultValidation)}
        />
      </div>
      
      {/* Helper text */}
      <div className="mt-2 text-xs text-gray-500">
        {!detectedCountry && !hasUserInput && (
          <p>Click to enter country code or start typing with your number</p>
        )}
        {!detectedCountry && hasUserInput && (
          <p>Enter country code (e.g., +1, +44, +91) to enable number field</p>
        )}
      </div>
      
    </div>
  );
};

// Default validation
const defaultValidation = {
  required: 'Phone number is required',
  validate: (value) => {
    if (!value) return 'Phone number is required';
    
    // Must have at least country code + some digits
    const digitsOnly = value.replace(/[^\d]/g, '');
    if (digitsOnly.length < 7) {
      return 'Please enter a complete phone number';
    }
    if (digitsOnly.length > 17) {
      return 'Phone number is too long';
    }
    
    return true;
  }
};